import { format } from "date-fns";
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "../../core/translation.core";
import { Order, PickupStatus } from "../../types/order";
import { pickup } from "../../core/order.core";

interface PickupDatePickerProps {
  isOpen: boolean;
  onClose: () => void;
  order: Order;
}

export function PickupDatePicker({ isOpen, onClose, order }: PickupDatePickerProps) {
  const { msg, t } = useTranslation();
  const queryClient = useQueryClient();
  const [isUpdating, setIsUpdating] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(order.pickupDate ? new Date(order.pickupDate) : null);
  const [selectedStatus, setSelectedStatus] = useState<PickupStatus>(order.pickupStatus as PickupStatus);

  if (!isOpen) return null;

  const handleSave = async () => {
    if (!selectedDate) return;

    if (selectedStatus === "pickup_completed" && order.paymentStatus !== "paid") {
      console.error("Cannot complete pickup status when payment is not fully paid");
      return;
    }

    try {
      setIsUpdating(true);
      await pickup({
        orderId: order.id,
        pickupDate: format(selectedDate, "yyyy-MM-dd"),
        pickupStatus: selectedStatus,
      });
      // Invalidate all queries that include 'orders' in their key
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      onClose();
    } catch (error) {
      console.error("Failed to update pickup status:", error);
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className="absolute right-0 top-full mt-2 bg-white dark:bg-her-darkblue2 rounded-lg shadow-lg p-4 z-50 min-w-[300px]">
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-gray-700 dark:text-white">{t(msg.order.pickupDate)}</label>
          <input
            type="date"
            value={selectedDate ? format(selectedDate, "yyyy-MM-dd") : ""}
            onChange={(e) => setSelectedDate(e.target.value ? new Date(e.target.value) : null)}
            className="border dark:border-gray-600 rounded-md p-2 bg-transparent dark:bg-her-darkblue dark:text-white focus:outline-none focus:ring-1 focus:ring-her-blue dark:focus:ring-blue-500"
          />
        </div>
        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-gray-700 dark:text-white">{t(msg.order.pickupStatus)}</label>
          <select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value as PickupStatus)}
            className="border dark:border-gray-600 rounded-md p-2 bg-transparent dark:bg-her-darkblue dark:text-white focus:outline-none focus:ring-1 focus:ring-her-blue dark:focus:ring-blue-500"
          >
            <option value="NA" className="dark:bg-her-darkblue">
              {t(msg.order.NA)}
            </option>
            <option value="awaiting_pickup" className="dark:bg-her-darkblue">
              {t(msg.order.awaitingPickup)}
            </option>
            <option value="pickup_completed" className="dark:bg-her-darkblue" disabled={order.paymentStatus !== "paid"}>
              {t(msg.order.pickupCompleted)}
            </option>
          </select>
        </div>
        <div className="flex justify-end gap-2 mt-2">
          <button
            onClick={onClose}
            className="px-3 py-1 rounded-md text-sm font-medium bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
          >
            {t(msg.form.cancel)}
          </button>
          <button
            onClick={handleSave}
            disabled={!selectedDate || isUpdating}
            className="px-3 py-1 rounded-md text-sm font-medium bg-her-blue text-white hover:bg-blue-600 disabled:opacity-50 dark:bg-blue-600 dark:hover:bg-blue-500"
          >
            {isUpdating ? t(msg.form.loading) : t(msg.form.save)}
          </button>
        </div>
      </div>
    </div>
  );
}
